import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { StoreInterfaceService } from '../../services/store-interface.service';
import { ChoiceListElement } from '../../types/choice-list-element.interface';
import { ChoiceList } from '../../types/choice-list.interface';
import { EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

@Component({
  selector: 'app-choice-list-element-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, InputNumberModule, GenericEntityFormComponent],
  templateUrl: './choice-list-element-form.component.html',
})
export class ChoiceListElementFormComponent implements OnInit {
  @Input() entityDefinition?: EntityDefinition<ChoiceListElement>;
  @Input() entity?: ChoiceListElement;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<ChoiceListElement>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<ChoiceListElement>();

  protected readonly storeService = inject(StoreInterfaceService);
  private readonly translateService = inject(TranslocoService);

  private readonly config = inject<DynamicDialogConfig<EntityDialogData<ChoiceListElement>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<ChoiceListElementFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  ngOnInit(): void {
    const data = this.config?.data;
    if (!data) {
      return;
    }
    this.entityDefinition = data.entityDefinition;
    this.mode = data.mode === 'edit' ? 'edit' : 'create';
    if (data.entity !== undefined) this.entity = data.entity;
    if (data.parentId !== undefined) this.parentId = data.parentId;
    if (data.additionalData !== undefined) this.additionalData = data.additionalData;
    if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
  }

  onFormSubmit(formData: EntityFormValue<ChoiceListElement>): void {
    const payload: EntityFormValue<ChoiceListElement> = { ...formData };

    if (this.mode === 'create' && this.parentId) {
      payload['choiceListId'] = this.parentId;

      const sortNr = payload['sortNr'];
      if (sortNr === undefined || sortNr === null) {
        payload['sortNr'] = this.calculateNextSortNr();
      }

      this.updateParentListHasElement();
    }

    if (this.dialogRef) {
      this.dialogRef.close(payload);
    } else {
      this.formSubmit.emit(payload);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  private directDeleteCallback?: (entity: ChoiceListElement) => void;

  onDeleteRequest(entity: ChoiceListElement): void {
    if (this.directDeleteCallback) {
      this.directDeleteCallback(entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<ChoiceListElement> = { __action: 'delete', entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(entity);
    }
  }

  private calculateNextSortNr(): number {
    if (!this.parentId) return 10;

    try {
      const elementsSignal = this.storeService.getData<ChoiceListElement>('ChoiceListElement');
      if (!elementsSignal) return 10;

      const elements = elementsSignal();
      const listElements = elements.filter(e => e.choiceListId === this.parentId);

      if (listElements.length > 0) {
        const maxSortNr = Math.max(...listElements.map(e => e.sortNr ?? 0));
        return maxSortNr + 10;
      } else {
        return 10;
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.orderNumberDeterminationError'), error);
      return 10;
    }
  }

  private updateParentListHasElement(): void {
    if (!this.parentId) return;

    try {
      const choiceListStore = this.storeService.getStore('ChoiceList');
      if (choiceListStore) {
        const parent = choiceListStore.byId()(this.parentId);

        if (parent && !parent.hasElement) {
          this.storeService.updateEntity<'ChoiceList', ChoiceList>('ChoiceList', parent.id, { hasElement: true });
        }
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.parentListUpdateError'), error);
    }
  }
}
